/**
 * UtteranceRow - 発話 1 行表示（セクション分類用）
 * PoC frontend/src/section-classifier/components/UtteranceRow.tsx から移植
 */

import type { TranscriptUtterance } from '../types';

interface UtteranceRowProps {
  utterance: TranscriptUtterance;
  index: number;
  isHighlighted: boolean;
  sectionColor?: string;
  borderColor?: string;
  onClick?: () => void;
  clickable?: boolean;
}

function formatShortTimestamp(timestamp: string): string {
  const parts = timestamp.split(':');
  if (parts.length === 3) {
    const hours = Number(parts[0]);
    if (!Number.isNaN(hours) && hours > 0) {
      return `${parts[0]}:${parts[1]}:${parts[2]}`;
    }
    return `${parts[1]}:${parts[2]}`;
  }
  return timestamp;
}

export function UtteranceRow({
  utterance,
  index,
  isHighlighted,
  sectionColor,
  borderColor,
  onClick,
  clickable = false,
}: UtteranceRowProps) {
  const isFacilitator = utterance.speaker === 'facilitator';
  const shortTime = formatShortTimestamp(utterance.timestamp);

  const bgClass = sectionColor || (isHighlighted ? 'bg-blue-50' : '');
  const borderClass = borderColor || (isHighlighted ? 'border-l-blue-500' : 'border-l-transparent');

  return (
    <div
      className={`flex items-start px-3 py-2 border-l-4 transition-colors ${bgClass} ${borderClass} ${
        clickable ? 'cursor-pointer hover:brightness-95' : ''
      }`}
      onClick={onClick}
    >
      <span className="text-xs text-gray-400 font-mono w-6 flex-shrink-0 pt-0.5 select-none">
        {index + 1}
      </span>

      <span className="font-mono text-gray-500 text-xs w-14 flex-shrink-0 pt-0.5">
        {shortTime}
      </span>

      <span className={`mr-2 flex-shrink-0 pt-0.5 ${isFacilitator ? 'text-blue-500' : 'text-green-500'}`}>
        {isFacilitator ? (
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
          </svg>
        ) : (
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
          </svg>
        )}
      </span>

      <div className="flex-1 min-w-0">
        <span className={`text-xs font-medium mr-1.5 ${isFacilitator ? 'text-blue-600' : 'text-green-600'}`}>
          {utterance.speakerName}
        </span>
        <span className="text-sm text-gray-700">{utterance.text}</span>
      </div>
    </div>
  );
}
